import { useQuery } from '@tanstack/react-query';
import { leaderboardApi } from '../lib/api';
import { useUserStore } from '../stores/userStore';
import { useLanguage } from '../hooks/useLanguage';
import { clsx } from 'clsx';

export function LeaderboardPage() {
  const { user } = useUserStore();
  const { t } = useLanguage();

  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => leaderboardApi.get(20),
  });

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold text-tg">{t('leaderboard')}</h1>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-tg rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-tg-secondary rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-tg-secondary rounded w-1/3" />
                  <div className="h-3 bg-tg-secondary rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {(leaderboard as any[])?.map((entry: any, index: number) => {
            const isCurrentUser = entry.user_id === user?.id;
            const rankDisplay =
              index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `${index + 1}th`;

            return (
              <div
                key={entry.user_id}
                className={clsx(
                  'bg-tg rounded-xl p-4',
                  isCurrentUser && 'ring-2 ring-brand-primary'
                )}
              >
                <div className="flex items-center gap-3">
                  {/* Rank */}
                  <div
                    className={clsx(
                      'w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm',
                      index === 0 && 'bg-yellow-100 text-yellow-700',
                      index === 1 && 'bg-gray-100 text-gray-600',
                      index === 2 && 'bg-orange-100 text-orange-700',
                      index > 2 && 'bg-tg-secondary text-tg-hint'
                    )}
                  >
                    {rankDisplay}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-tg truncate">
                      {entry.username ? `@${entry.username}` : entry.first_name}
                      {isCurrentUser && ` (${t('you')})`}
                    </p>
                    <p className="text-xs text-tg-hint">
                      {entry.tasks_completed} {t('tasks_completed')}
                    </p>
                  </div>

                  {/* Earnings */}
                  <div className="text-right rtl:text-left">
                    <p className="font-bold text-brand-primary">
                      ${(entry.total_earned_usd / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-tg-hint">
                      {t('rep')}: {entry.reputation_score}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
